import AppConfig from '@/AppConfig';
import type { SavedPaymentMethod } from '@/classes/stripe';
import axios from 'axios';
import { getOtherSavedMethods } from './paymentMethodList';

/**
 * Service for the Billing saved payment methods feature. Calls the system
 * backend (checkout-api), which talks to Stripe server-side. Same-fingerprint
 * duplicates are preserved so owners can remove redundant Stripe records.
 */
export default class PaymentMethodService {
  private static get paymentMethodsUrl(): string {
    return `${AppConfig.getAPIBaseUrl()}/subscription/stripe/payment-methods`;
  }

  /**
   * Fetches the raw list of saved payment methods from the backend.
   * Tolerates both a bare array response and a `{ data: [...] }` envelope.
   */
  private static async fetchCards(): Promise<SavedPaymentMethod[]> {
    const response = await axios.get<SavedPaymentMethod[] | { data: SavedPaymentMethod[] }>(
      PaymentMethodService.paymentMethodsUrl
    );
    const payload = response?.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    return payload?.data ?? [];
  }

  /**
   * Returns all saved payment methods from the API (including same-fingerprint
   * duplicates).
   */
  static async listPaymentMethods(): Promise<SavedPaymentMethod[]> {
    return PaymentMethodService.fetchCards();
  }

  /**
   * Returns the non-default saved methods for the "Other saved methods" list,
   * sorted by creation date descending (duplicates kept).
   */
  static async listOtherSavedMethods(): Promise<SavedPaymentMethod[]> {
    return getOtherSavedMethods(await PaymentMethodService.fetchCards());
  }

  /**
   * Detaches a non-default saved payment method. The backend rejects deleting
   * the current default and read-only legacy types.
   */
  static async deletePaymentMethod(id: string): Promise<void> {
    await axios.delete(`${PaymentMethodService.paymentMethodsUrl}/${id}`);
  }

  /**
   * Sets a manageable saved payment method as the account default for future
   * charges. The backend rejects expired and read-only legacy types.
   */
  static async setDefaultPaymentMethod(id: string): Promise<void> {
    await axios.patch(`${PaymentMethodService.paymentMethodsUrl}/${id}`);
  }
}
